import React, { useEffect, useState } from 'react';
import {
  DEFAULT_AVATAR_URL,
  getConventionAvatarForUser,
  useAvatarUrl,
} from '../lib/wrapped/avatarUtils';
import type { AvatarHonorStyle } from '../lib/daisySeal';
import { DAISY_SEAL_TOOLTIP } from '../lib/daisySeal';
import { getLedgerAvatarCrop, SHIELD_PATH } from '../lib/ledgerAvatarCrop';

interface WingoLedgerAvatarProps {
  username: string;
  /** Unique id for SVG defs (e.g. rank or username slug). */
  instanceId: string | number;
  honorStyle?: AvatarHonorStyle;
  onClick?: () => void;
  className?: string;
}

const WRAPPER_STYLE: React.CSSProperties = { width: '2.5rem', height: '3rem' };

/** Shared honor stroke — blue matches yellow exactly. */
const HONOR_STROKE_WIDTH = 3.8;

/**
 * Yellow website recipe; blue is a 1:1 hue remap.
 * No floating shine dash (stroke-dasharray animate) — static bevels only.
 */
const HONOR_LOOK = {
  yellowstone: {
    borderGradient: 'yellow-border-gradient',
    bevelFilter: 'yellow-bevel-soft',
    outerBevel: '#FFF8C6',
    innerBevel: '#8B6508',
  },
  'daisy-seal': {
    borderGradient: 'daisy-border-gradient',
    bevelFilter: 'daisy-bevel-soft',
    // 1:1 roles with yellow (#FFF8C6 / #8B6508)
    outerBevel: '#38BDF8',
    innerBevel: '#075985',
  },
} as const;

export const WingoLedgerAvatar: React.FC<WingoLedgerAvatarProps> = ({
  username,
  instanceId,
  honorStyle = 'default',
  onClick,
  className = '',
}) => {
  const id = String(instanceId).replace(/[^a-zA-Z0-9_-]/g, '_');
  const { y: avatarY, height: avatarHeight } = getLedgerAvatarCrop(username);
  const look =
    honorStyle === 'daisy-seal' || honorStyle === 'yellowstone'
      ? HONOR_LOOK[honorStyle]
      : null;
  const tooltip = honorStyle === 'daisy-seal' ? DAISY_SEAL_TOOLTIP : undefined;
  const resolvedSrc = useAvatarUrl(username);
  const [avatarSrc, setAvatarSrc] = useState(resolvedSrc);

  useEffect(() => {
    setAvatarSrc(resolvedSrc);
  }, [resolvedSrc]);

  return (
    <div
      className={`relative flex-shrink-0 cursor-pointer hover:scale-110 transition-transform duration-200 ${className}`}
      style={WRAPPER_STYLE}
      title={tooltip}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <img
        src={avatarSrc}
        alt=""
        className="hidden"
        onError={(e) => {
          if (avatarSrc === DEFAULT_AVATAR_URL) return;
          const failed = e.currentTarget.currentSrc || e.currentTarget.src;
          const expected = new URL(avatarSrc, window.location.href).href;
          if (failed !== expected) return;
          if (avatarSrc.includes('/api/avatar')) {
            setAvatarSrc(getConventionAvatarForUser(username));
            return;
          }
          setAvatarSrc(DEFAULT_AVATAR_URL);
        }}
      />
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 40 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
        aria-hidden
      >
        <defs>
          <clipPath id={`avatar-clip-${id}`}>
            <path d={SHIELD_PATH} />
          </clipPath>
          <linearGradient id={`border-gradient-${id}`} x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#B02E0C" />
            <stop offset="25%" stopColor="#FF7A00" />
            <stop offset="50%" stopColor="#FFA733" />
            <stop offset="75%" stopColor="#FF7A00" />
            <stop offset="100%" stopColor="#B02E0C" />
          </linearGradient>
          <linearGradient id={`yellow-border-gradient-${id}`} x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#B8860B" />
            <stop offset="20%" stopColor="#DAA520" />
            <stop offset="50%" stopColor="#FFD700" />
            <stop offset="80%" stopColor="#DAA520" />
            <stop offset="100%" stopColor="#B8860B" />
          </linearGradient>
          {/* Parallel stops to yellow: dark → mid → bright → mid → dark (no neon icy mid). */}
          <linearGradient id={`daisy-border-gradient-${id}`} x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#0369A1" />
            <stop offset="20%" stopColor="#0284C7" />
            <stop offset="50%" stopColor="#0EA5E9" />
            <stop offset="80%" stopColor="#0284C7" />
            <stop offset="100%" stopColor="#0369A1" />
          </linearGradient>
          <filter id={`yellow-bevel-soft-${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.4" result="soft" />
            <feMerge>
              <feMergeNode in="soft" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`daisy-bevel-soft-${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.4" result="soft" />
            <feMerge>
              <feMergeNode in="soft" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          d={SHIELD_PATH}
          stroke={
            look
              ? `url(#${look.borderGradient}-${id})`
              : `url(#border-gradient-${id})`
          }
          strokeWidth={look ? HONOR_STROKE_WIDTH : 3}
          fill="white"
          pointerEvents="none"
        />

        {look && (
          <>
            <path
              d={SHIELD_PATH}
              stroke={look.outerBevel}
              strokeWidth="1.8"
              fill="none"
              opacity="0.8"
              pointerEvents="none"
              filter={`url(#${look.bevelFilter}-${id})`}
              transform="translate(20 24) scale(1.025) translate(-20 -24)"
            />
            <path
              d={SHIELD_PATH}
              stroke={look.innerBevel}
              strokeWidth="1.4"
              fill="none"
              opacity="0.6"
              pointerEvents="none"
              filter={`url(#${look.bevelFilter}-${id})`}
              transform="translate(20 24) scale(0.98) translate(-20 -24)"
            />
          </>
        )}

        <image
          key={avatarSrc}
          href={avatarSrc}
          x="2"
          y={avatarY}
          width="36"
          height={avatarHeight}
          clipPath={`url(#avatar-clip-${id})`}
          preserveAspectRatio="xMidYMid slice"
        />
      </svg>
    </div>
  );
};

export default WingoLedgerAvatar;
